/* eslint-disable no-use-before-define */
/* eslint-disable import/no-mutable-exports */
/* eslint-disable camelcase */
/* eslint-disable no-underscore-dangle */

import { PhoneNumberUtil } from 'google-libphonenumber';
import { isEqual, cloneDeep, isEmpty } from 'lodash';
import Mustache from 'mustache';
import JSZip from 'jszip';

import { parsedInteger, transformProducts } from '../builder/components/modals/inventory/inventory.util';
import UiUploader from '../../shared/uploader';
import { SubmissionUtils } from './submission';
import {
  emailTestPatterUnicode,
  getEmptyTableFields,
  generateFileInfo,
  getFormattedDate,
  fplsFetch
} from '../../shared/utils';
import {
  GOOGLE_SHEET_MAX_CELL_CHARACTER,
  setTableCtrlValueToNull,
  ctrlDefaults,
  DateTimeType,
  ControlType,
  InputType
} from '../../shared/form-lib';

export const uploader = new UiUploader(); // This export was made to maintain the same instance of the class in file_upload.jsx

const websiteTestPattern =
  /^(?:(?:https?|ftp):\/\/)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/;

export const isOnline = async (url = 'https://www.google.com') => {
  // moved here due to circular imports
  const online = navigator.onLine;
  if (online) {
    try {
      const response = await fetch(url, { method: 'HEAD', mode: 'no-cors' });
      return response.status === 0;
    } catch {
      return false;
    }
  }
  return online;
};

export const isExtensionSupported = (fileType) => {
  const supportedFileExtensions = [
    // IMAGES
    'image/gif',
    'image/x-icon',
    'image/jpeg',
    'image/png',
    'image/svg+xml',
    'image/tiff',
    'image/webp',
    // VIDEOS:
    'video/x-msvideo',
    'video/mpeg',
    'video/ogg',
    'video/webm',
    'video/3gpp',
    'video/3gpp2',
    'video/mp4',
    'video/avi',
    'video/x-flv',
    'video/x-matroska',
    'video/quicktime',
    // DOCUMENTS
    'application/x-abiword',
    'application/octet-stream',
    'text/csv',
    'application/vnd.oasis.opendocument.presentation',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/vnd.oasis.opendocument.text',
    'application/pdf',
    'application/x-shockwave-flash',
    'text/plain',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.visio',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    // Others
    'application/zip',
    'application/x-rar-compressed',
    'application/x-7z-compressed',
    'application/x-tar',
    'application/gzip',
  ];
  if (supportedFileExtensions.includes(fileType.toLowerCase())) {
    return true;
  }
  return false;
};

export const decimalCount = (num) => {
  // Convert to String
  const numStr = String(num).toLocaleString();

  // String Contains Decimal
  if (numStr.includes('.')) {
    return numStr.split('.')[1].length;
  }

  // String is exponential
  if (numStr.includes('e')) {
    const after_e = numStr.substring(numStr.indexOf('e') + 1);
    return Math.abs(after_e);
  }

  // String Does Not Contain Decimal
  return 0;
};

export const formatDateToReadable = (date) => {
  const year = date.getFullYear();
  const month = date.toLocaleString('en-US', { month: 'long' });
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();

  // Determine AM or PM
  const amOrPm = hours >= 12 ? 'PM' : 'AM';

  // Convert hours to 12-hour format
  const hours12 = hours % 12 || 12; // If hours is 0, set it to 12 for 12:00 AM or 12:00 PM

  const formattedDate = `${month} ${day}, ${year} ${hours12}:${String(minutes).padStart(2, '0')} ${amOrPm}`;

  // FORMAT: September 28, 2023 2:06 PM
  return formattedDate;
};

export const isValidTimeString = (value) => {
  // if time value in 'hh:mm' format
  const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;
  if (!timePattern.test(value)) {
    return false; // Not a valid time format
  }

  const [hours, minutes] = value.split(':');
  const numericHours = parseInt(hours, 10);
  const numericMinutes = parseInt(minutes, 10);

  if (numericHours < 0 || numericHours > 23 || numericMinutes < 0 || numericMinutes > 59) {
    return false; // Hours or minutes are out of valid range
  }

  return true;
};

export const zipFiles = async (file) => {
  const promise = await new Promise((resolve) => {
    const zip = new JSZip();
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = () => {
      const arrBuffer = reader.result;
      zip.file(file.name, arrBuffer);
      const zipBlob = zip.generate({ type: 'blob' });
      const fileName = `${file.name.replace(/\.[^/.]+$/, '')}.zip`;
      const zipFile = new File([zipBlob], fileName, {
        lastModifiedDate: file.lastModifiedDate,
        lastModified: file.lastModified,
      });
      resolve([zipFile]);
    };
  });
  return promise;
};

const dataURItoBlob = (dataURI) => {
  // convert base64/URLEncoded data component to raw binary data held in a string
  let byteString;
  if (dataURI.split(',')[0].indexOf('base64') >= 0) {
    byteString = atob(dataURI.split(',')[1]);
  } else {
    byteString = unescape(dataURI.split(',')[1]);
  }
  // separate out the mime component
  // write the bytes of the string to a typed array
  const ia = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i += 1) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ia], { type: 'image/png' });
};

const getSpecialLabel = (propertyName, replacers, customTexts) => {
  let customFormat = customTexts[propertyName];
  const replacementKeys = Object.keys(replacers);
  for (let i = 0; i < replacementKeys.length; i += 1) {
    customFormat = customFormat.replace(replacementKeys[i], replacers[replacementKeys[i]]);
  }
  return customFormat;
};

const getAbsoluteNumberLength = (value) => {
  if (Math.sign(value) === -1) {
    return value.toString().length - 1;
  }
  return value.toString().length;
};

export const getStateValue = (value, col, tr, inTable, inBuilder) => {
  if (inTable) {
    if (inBuilder) return '';
    return value ? value?.[tr - 1]?.[col - 1] : '';
  }
  return value ?? '';
};

export const getLookupOptions = async (ctrl) => {
  const payload = {
    form_field: ctrl.props.form_field.split(':').pop(),
    form: ctrl.props.form,
    sort: ctrl.props.sort,
  };

  try {
    const response = await fplsFetch({
      url: '/api/v2/public/lookup/basic',
      removeToken: true,
      method: 'POST',
      data: payload,
    });

    return { response: response.options, error: null };
  } catch (error) {
    const errorMessage = error?.message || 'An error has occurred when attempting to retrieve form responses.';
    console.error(`Error while loading form responses (id:${ctrl.props.form}). Details:`, error);

    return { response: null, error: errorMessage };
  }
};

export const getAutoFillLookupValues = async ({sourceFields, query, ctrl}) => {
  const payload = {
    form_field: ctrl.props.form_field.split(':').pop(),
    dest_field_ids: ctrl.props.dest_field_ids,
    current_form: window.FORM_ID.toString(),
    source_field_ids: sourceFields,
    query: query.toString(),
    form: ctrl.props.form,
  };

  try {
    const response = await fplsFetch({
      url: '/api/v2/public/lookup/autofill',
      removeToken: true,
      method: 'POST',
      data: payload,
    });

    const autoFillValues = {};
    for (let i = 0; i < response.field_maps.length; i += 1) {
      autoFillValues[response.field_maps[i].id] = response.field_maps[i].value;
    }

    return { autoFillValues, error: null };
  } catch (error) {
    const errorMessage = error?.message || 'An error has occurred when attempting to retrieve form responses.';
    console.error(`Error while loading form responses (id:${ctrl.props.form}). Details:`, error);

    return { autoFillValues: null, error: errorMessage };
  }
};

// Log payment transaction response to server
export const logToServer = async (payload) => {
  try {
    const response = await fplsFetch({
      method: 'POST',
      url: `/api/v2/public/payment_log/${window.FORM_ID}`,
      data: payload,
      removeToken: true,
    });

    if (response && response.status === 'OK') console.log(response);
  } catch (error) {
    console.log(error);
  }
};

const shuffleWithinGroups = (controls, isFixed) => {
  const grouped = [];
  let currentGroup = [];

  // Group items based on fixed items
  controls.forEach((ctrl) => {
    if (isFixed.includes(ctrl.type)) {
      // Push the current group and start a new one
      if (currentGroup.length > 0) {
        grouped.push({ fixed: false, items: currentGroup });
        currentGroup = [];
      }
      grouped.push({ fixed: true, items: [ctrl] }); // Add the fixed ctrl as its own group
    } else {
      currentGroup.push(ctrl); // Collect shufflable items
    }
  });

  // Push the last group if any
  if (currentGroup.length > 0) {
    grouped.push({ fixed: false, items: currentGroup });
  }

  // Shuffle only non-fixed groups
  grouped.forEach((group) => {
    if (!group.fixed) {
      for (let i = group.items.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [group.items[i], group.items[j]] = [group.items[j], group.items[i]];
      }
    }
  });

  // Flatten the groups back into a single array
  return grouped.flatMap((group) => group.items);
}

const shuffleOptions = (newCtrl) => {
  const { options } = newCtrl.props;
  const { weights } = newCtrl.props;
  let index = options?.length;
  let rnd;
  let tmp1;
  if (newCtrl.type === ControlType.IMAGE_CHOICE) {
    const { images } = newCtrl.props;
    let tmp2;
    let tmp3;
    while (index) {
      rnd = Math.floor(Math.random() * index);
      index -= 1;
      tmp1 = options[index];
      tmp2 = images[index];
      options[index] = options[rnd];
      images[index] = images[rnd];
      options[rnd] = tmp1;
      images[rnd] = tmp2;
      if (newCtrl.props.option_values) {
        tmp3 = weights[index];
        weights[index] = weights[rnd];
        weights[rnd] = tmp3;
      }
    }
    newCtrl.props.options = options;
    newCtrl.props.images = images;
    newCtrl.props.weights = weights;
  } else if (newCtrl.type === ControlType.RADIO_BUTTONS || ControlType.CHECKBOXES) {
    let tmp4;
    while (index) {
      rnd = Math.floor(Math.random() * index);
      index -= 1;
      tmp1 = options[index];
      options[index] = options[rnd];
      options[rnd] = tmp1;
      if (newCtrl.props.option_values) {
        tmp4 = weights[index];
        weights[index] = weights[rnd];
        weights[rnd] = tmp4;
      }
    }
    newCtrl.props.options = options;
    newCtrl.props.weights = weights;
  }
  return newCtrl;
};

const phoneNumberValidate = (ctrl, customTexts, phoneNumberData) => {
  const { phoneNumber, regionCode } = phoneNumberData;
  let defaultRegionCode = '';
  let phoneErr = '';

  if (ctrl.props.phone_default_code) {
    defaultRegionCode = ctrl.props.phone_default_code;
  }

  if (regionCode) {
    try {
      // Validating possible phone numbers for all countries/regions of the world.
      const phoneUtil = PhoneNumberUtil.getInstance();
      const invalidPhonenumber = !phoneUtil.isPossibleNumber(phoneUtil.parse(phoneNumber));

      if (invalidPhonenumber) {
        phoneErr = customTexts.invalid_phone_no.trim();
      }
      if (ctrl.props.phone_codes_limit) {
        if (regionCode !== defaultRegionCode || !defaultRegionCode) {
          if (ctrl.props.phone_valid_codes.length > 0) {
            const no = ctrl.props.phone_valid_codes.indexOf(regionCode.toLowerCase());
            if (no === -1) {
              phoneErr = customTexts.invalid_phone_no.trim();
            }
          }
        }
      }
    } catch (e) {
      phoneErr = customTexts.invalid_phone_no.trim();
    }
  }

  return phoneErr;
};

const validateAddressField = (ctrl) => {
  const { street_address_line_1, street_address_line_2, postal_code, city, state, address_country } = ctrl.props;
  
  const errorMessages = [];

  if (street_address_line_1.display && street_address_line_1.required && (!ctrl.value || !ctrl.value.street_address_line_1.value)) {
    errorMessages.push(street_address_line_1.label);
  }
  if (street_address_line_2.display && street_address_line_2.required && (!ctrl.value || !ctrl.value.street_address_line_2.value)) {
    errorMessages.push(street_address_line_2.label);
  }
  if (postal_code.display && postal_code.required && (!ctrl.value || !ctrl.value.postal_code.value)) {
    errorMessages.push(postal_code.label);
  }
  if (city.display && city.required && (!ctrl.value || !ctrl.value.city.value)) {
    errorMessages.push(city.label);
  }
  if (state.display && state.required && (!ctrl.value || !ctrl.value.state.value)) {
    errorMessages.push(state.label);
  }
  if (address_country.display && address_country.required && (!ctrl.value || !ctrl.value.address_country.value)) {
    errorMessages.push(address_country.label);
  }

  // Check if there are error messages to construct the final error string
  if (errorMessages.length > 0) {
    const errorMessage = errorMessages.length > 1 ? 'Values are required for' : 'Value is required for';
    return `${errorMessage} ${errorMessages.join(', ')}`;
  } else {
    return null; // No errors
  }
};

export const getYearMonthDayTime = (dateTime, separator, type, timeFormat, dateFormat) => {
  const formattedDate = getFormattedDate(dateTime, dateFormat, separator);
  const formattedTime = dateTime.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: timeFormat === '12',
  });

  let date;

  if (type === DateTimeType.DATETIME) {
    date = `${formattedDate} ${formattedTime}`;
  } else if (type === DateTimeType.DATE) {
    date = formattedDate;
  } else if (type === DateTimeType.TIME) {
    date = formattedTime;
  }

  return date;
};

export const validateTableField = (ctrl, customTexts) => {
  const newCtrl = cloneDeep(ctrl);
  if (!newCtrl.value && !newCtrl._validationError) {
    newCtrl.value = getEmptyTableFields(ctrl.props.min_rows, ctrl.props.columns);
    newCtrl._validationError = getEmptyTableFields(ctrl.props.min_rows, ctrl.props.columns);
  } else if (!newCtrl._validationError || (newCtrl.value && newCtrl._validationError)) {
    newCtrl._validationError = getEmptyTableFields(newCtrl.value.length, ctrl.props.columns);
  }
  const ctrlValue = newCtrl.value;
  const ctrlValidationError = newCtrl._validationError;
  const columnTypes = newCtrl.props.column_types;
  let tableError = '';
  let replacers;
  let valErr;
  for (let i = 0; i < ctrlValue.length; i += 1) {
    const row = ctrlValue[i];
    for (let j = 0; j < row.length; j += 1) {
      let cellAnswer = row[j];
      const props = newCtrl.table_props[j];
      if (!cellAnswer) {
        if (props && props.required) {
          ctrlValidationError[i][j] = customTexts.required_field;
          tableError = customTexts.required_field;
        } else {
          ctrlValidationError[i][j] = '';
        }
      } else {
        if (props?.required) {
          ctrlValidationError[i][j] = '';
        }
        const fieldType = columnTypes[j];
        if (fieldType === 'email') {
          if (!emailTestPatterUnicode.test(cellAnswer.trim())) {
            ctrlValidationError[i][j] = customTexts.invalid_email;
            tableError = customTexts.invalid_email;
          } else {
            ctrlValidationError[i][j] = '';
          }
        } else if (fieldType === 'number_input') {
          const isnum = /^[-]?[0-9]+(\.[0-9]+)?$/;
          cellAnswer = String(cellAnswer);
          row[j] = cellAnswer;
          if (!isnum.test(cellAnswer.trim())) {
            ctrlValidationError[i][j] = customTexts.invalid_number;
            tableError = customTexts.invalid_number;
          } else if (
            props.hasOwnProperty('min_count') &&
            props.min_count > 0 &&
            getAbsoluteNumberLength(cellAnswer) < props.min_count
          ) {
            replacers = {
              '{{ minimum }}': props.min_count,
            };
            valErr = ` ${getSpecialLabel('min_characters', replacers, customTexts)}`;
            ctrlValidationError[i][j] = valErr;
            tableError = valErr;
          } else if (
            props.hasOwnProperty('max_count') &&
            props.max_count > 0 &&
            getAbsoluteNumberLength(cellAnswer) > props.max_count
          ) {
            replacers = {
              '{{ maximum }}': props.max_count,
            };
            valErr = ` ${getSpecialLabel('max_characters', replacers, customTexts)}`;
            ctrlValidationError[i][j] = valErr;
            tableError = valErr;
          } else if (
            props.hasOwnProperty('min_acceptable') &&
            props.min_acceptable > 0 &&
            cellAnswer < props.min_acceptable
          ) {
            valErr = ` Please enter a value not less than ${props.min_acceptable}.`;
            ctrlValidationError[i][j] = valErr;
            tableError = valErr;
          } else if (
            props.hasOwnProperty('max_acceptable') &&
            props.max_acceptable > 0 &&
            cellAnswer > props.max_acceptable
          ) {
            valErr = ` Please enter a value not greater than ${props.max_acceptable}.`;
            ctrlValidationError[i][j] = valErr;
            tableError = valErr;
          } else {
            ctrlValidationError[i][j] = '';
          }
        } else if (fieldType === 'text_input') {
          if (props.hasOwnProperty('min_length') && props.min_length > 0 && cellAnswer.length < props.min_length) {
            valErr = ` Please enter at least ${props.min_length} characters.`;
            ctrlValidationError[i][j] = valErr;
            tableError = valErr;
          } else if (
            props.hasOwnProperty('max_length') &&
            props.max_length > 0 &&
            cellAnswer.length > props.max_length
          ) {
            valErr = ` Please enter no more than ${props.max_length} characters.`;
            ctrlValidationError[i][j] = valErr;
            tableError = valErr;
          } else {
            ctrlValidationError[i][j] = '';
          }
        }
      }
    }
  }
  return { tableError, newCtrl };
};

const checkProductAvailability = async (selectedProducts, formId) => {
  const isOutOfStock = (selectedQuantity, availableQuantity) => {
    const parsedQuantity = parsedInteger(availableQuantity);
    if (parsedQuantity === undefined) return true;
    return selectedQuantity > parsedQuantity;
  };

  const validationError = {};

  try {
    const json = await fplsFetch({ url: `/api/v2/public/${Number(formId)}/inventory_product/all` });
    const products = transformProducts.toFrontEndShape(json.products || []);

    selectedProducts.forEach((product) => {
      const { quantity: selectedQuantity, combination: selectedCombination } = product.selection;
      const storeProduct = products.find(({ id }) => id === product.id);
      let errorMessage = 'Out of stock';
      const { id } = product;

      if (!storeProduct) {
        validationError[id] = errorMessage;
        return;
      }

      const { stock_quantity, variants } = storeProduct;
      let availableQuantity = stock_quantity;

      if (!isEmpty(selectedCombination)) {
        const selectedVariantInStore = variants.find(({ combination }) => isEqual(combination, selectedCombination));
        if (!selectedVariantInStore) {
          validationError[id] = 'Option not found';
          return;
        }

        availableQuantity = selectedVariantInStore.quantity;
        errorMessage = 'Option out of stock';

        if (isOutOfStock(selectedQuantity, availableQuantity)) {
          validationError[id] = errorMessage;
        }
      } else if (isOutOfStock(selectedQuantity, availableQuantity)) {
        validationError[id] = errorMessage;
      }
    });
  } catch (error) {
    console.error('Product availability check failed. ', error);
  }

  return validationError;
};

/**
 * Validates a control's value based on its properties and type.
 *
 * @param {Object} props - The properties of the control to validate.
 * @param {string} [props.responseStore='formplus'] - The response store type.
 * @param {Function} props.setCtrlValidationError - Function to set the validation error message.
 * @param {boolean} props.isPayOnSubmission - Indicates if payment is required on submission.
 * @param {Object} [props.userCustomTexts] - Custom text messages for validation errors.
 * @param {Object} [props.phoneNumberData] - Data for validating phone numbers.
 * @param {Function} props.setNewCtrl - Function to update the control with new validation data.
 * @param {Object} props.ctrl - The control object to validate.
 * @param {number} [props.col] - The column index for table field validation.
 * @param {number} [props.tr] - The row index for table field validation.
 *
 * @returns {Object} - The validation result.
 * @returns {boolean} result.requiredOk - Indicates if the required validation passed.
 * @returns {boolean} result.validationOk - Indicates if the content validation passed.
 * @returns {Object} result.newControl - The updated control object with validation results.
 */
export const controlValidate = (props) => {
  const {
    responseStore = 'formplus',
    setCtrlValidationError,
    isPayOnSubmission,
    userCustomTexts,
    phoneNumberData,
    setNewCtrl,
    ctrl,
    col,
    tr
  } = props;

  const customTexts = userCustomTexts || {};
  const newControl = cloneDeep(ctrl);
  let validationOk = true;
  let requiredOk = true;
  let err = '';

  function checkCnt() {
    if (typeof ctrl.value === 'object') {
      return Object.keys(ctrl.value).reduce(function checkCntr(acc, key) {
        if (key === '__fpls_opt_other__') {
          return ctrl.__fpls_opt_other__ ? ++acc : acc;
        }
        return ctrl.value[key] ? ++acc : acc;
      }, 0);
    }
    return null;
  }

  if (ctrl.props.required) {
    const paymentCtrls = ['paypal', 'paystack', 'stripe', 'flutterwave'];
    const isRequired = isPayOnSubmission ? !paymentCtrls.includes(ctrl.type) : true;
    if (!ctrl.value && isRequired) {
      requiredOk = false;
    } else if (ctrl.type === 'matrix_rating') {
      if (ctrl.value && ctrl.value.length) {
        for (let j = 0; j < ctrl.props.row_headers.length; j += 1) {
          if (ctrl.value[j].split(': ')?.pop()?.trim() === 'undefined') {
            requiredOk = false;
          }
        }
      }
    } else if (ctrl.type === 'select_multiple' || ctrl.type === ControlType.SELECT_MULTIPLE) {
      if (ctrl.value === null || ctrl.value?.length === 0) {
        requiredOk = false;
      } else if (ctrl.value?.length > 0) {
        for (let k = 0; k < ctrl.value.length; k += 1) {
          if (!ctrl.value[k]) {
            requiredOk = false;
          }
        }
      }
    }
  }

  // Check for validation error when the main address field is not required but
  // May have atleast one required subfield (since address line 1 is always required)
  if (ctrl.type === 'address' && !ctrl.props.required) {
    const addressErr = validateAddressField(ctrl);
    err = addressErr;
    if (err) validationOk = false;
  }

  if (ctrl.value) {
    // content validation
    const ctrlHasType = ctrl.props.type || ctrl.type;
    const ctrlIsTextual = ['text_input', 'short_text', 'labeled_input', 'prepended_text', 'appended_text', 'email'];

    const ctrlIsNumber = ctrl.type === 'number_input';
    const isnum = /^[-]?[0-9]+(\.[0-9]+)?$/;

    if (ctrlIsNumber) {
      if (!isnum.test(ctrl.value.toString().trim())) {
        err = `${err} Please supply a number.`.trim();
        validationOk = false;
      }
    } else if (ctrlHasType && ctrlIsTextual.includes(ctrl.type)) {
      if (ctrl.props.type === InputType.NUMBER) {
        // labeled_input of type number
        if (!isnum.test(ctrl.value.toString().trim())) {
          err = (err + customTexts.invalid_number).trim();
          validationOk = false;
        }
      } else if (ctrl.props.type === InputType.EMAIL || ctrl.type === 'email') {
        if (!emailTestPatterUnicode.test(ctrl.value.toString().trim())) {
          err = (err + customTexts.invalid_email).trim();
          validationOk = false;
        }
      }
    } else if (ctrl.type === 'phone') {
      const phoneErr = phoneNumberValidate(ctrl, customTexts, phoneNumberData);
      err = phoneErr;
      if (err) validationOk = false;
    }

    if (ctrl.type === 'website') {
      if (!websiteTestPattern.test(ctrl.value.toString().trim())) {
        err = (err + customTexts.invalid_website).trim();
        validationOk = false;
      }
    }

    if (ctrl.type === 'lookup' && ctrl.props.field_type === 'email') {
      if (!emailTestPatterUnicode.test(ctrl.value.toString().trim())) {
        err = (err + customTexts.invalid_email).trim();
        validationOk = false;
      }
    }

    let replacers;
    let valErr;
    if (ctrl.props.min_length) {
      if (ctrl.value.length < ctrl.props.min_length) {
        replacers = {
          '{{ minimum }}': ctrl.props.min_length,
        };
        valErr = ` ${getSpecialLabel('min_characters', replacers, customTexts)}`;
        err = (err + valErr).trim();
        validationOk = false;
      }
    }

    if (ctrl.props.max_length) {
      if (ctrl.value.length > ctrl.props.max_length) {
        replacers = {
          '{{ maximum }}': ctrl.props.max_length,
        };
        valErr = ` ${getSpecialLabel('max_characters', replacers, customTexts)}`;
        err = (err + valErr).trim();
        validationOk = false;
      }
    }

    if (ctrl.props.min_selections) {
      if (checkCnt() < ctrl.props.min_selections) {
        replacers = {
          '{{ minimum }}': ctrl.props.min_selections,
          '{{ number }}': checkCnt(),
        };
        valErr = ` ${getSpecialLabel('min_selects', replacers, customTexts)}`;
        err = (err + valErr).trim();
        validationOk = false;
      }
    }

    if (ctrl.props.max_selections) {
      if (checkCnt() > ctrl.props.max_selections) {
        replacers = {
          '{{ maximum }}': ctrl.props.max_selections,
          '{{ number }}': checkCnt(),
        };
        valErr = ` ${getSpecialLabel('max_selects', replacers, customTexts)}`;
        err = (err + valErr).trim();
        validationOk = false;
      }
    }

    if (ctrl.props.min_word_length) {
      if (ctrl.value.trim().split(/\s+/).length < ctrl.props.min_word_length) {
        replacers = {
          '{{ minimum }}': ctrl.props.min_word_length,
        };
        valErr = ` ${getSpecialLabel('min_words', replacers, customTexts)}`;
        err = (err + valErr).trim();
        validationOk = false;
      }
    }

    if (ctrl.props.max_word_length) {
      if (ctrl.value.trim().split(/\s+/).length > ctrl.props.max_word_length) {
        replacers = {
          '{{ maximum }}': ctrl.props.max_word_length,
        };
        valErr = ` ${getSpecialLabel('max_words', replacers, customTexts)}`;
        err = (err + valErr).trim();
        validationOk = false;
      }
    }

    const length = getAbsoluteNumberLength(ctrl.value);
    if (ctrl.props.min_count) {
      if (length < ctrl.props.min_count) {
        replacers = {
          '{{ minimum }}': ctrl.props.min_count,
        };
        valErr = ` ${getSpecialLabel('min_characters', replacers, customTexts)}`;
        err = (err + ` ${valErr}`).trim();
        validationOk = false;
      }
    }

    if (ctrl.props.max_count) {
      if (length > ctrl.props.max_count) {
        replacers = {
          '{{ maximum }}': ctrl.props.max_count,
        };
        valErr = ` ${getSpecialLabel('max_characters', replacers, customTexts)}`;
        err = (err + valErr).trim();
        validationOk = false;
      }
    }

    if (ctrl.props.min_acceptable) {
      if (ctrl.value < ctrl.props.min_acceptable) {
        valErr = ` Please enter a value not less than ${ctrl.props.min_acceptable}.`;
        err = (err + valErr).trim();
        validationOk = false;
      }
    }

    if (ctrl.props.max_acceptable) {
      if (ctrl.value > ctrl.props.max_acceptable) {
        valErr = ` Please enter a value not greater than ${ctrl.props.max_acceptable}.`;
        err = (err + valErr).trim();
        validationOk = false;
      }
    }

    if (ctrl.type === 'table_field') {
      const { newCtrl } = validateTableField(ctrl, customTexts);
      if (setNewCtrl) setNewCtrl(newCtrl);
      err = newCtrl._validationError[tr - 1][col - 1];
      newCtrl._validationError?.forEach((arr) => {
        if (arr?.some((erx) => erx !== '')) validationOk = false;
      });
    }

    if (ctrl.type === 'address') {
      const addressErr = validateAddressField(ctrl);
      err = addressErr;
      if (err) validationOk = false;
    }
    
    const isGoogleSheetLimitExceeded = (
      (ctrl.value.length > GOOGLE_SHEET_MAX_CELL_CHARACTER) &&
      responseStore !== 'formplus'
    );   
    
    if (isGoogleSheetLimitExceeded) {
      replacers = {
        '{{ maximum }}': GOOGLE_SHEET_MAX_CELL_CHARACTER,
      };
      valErr = ` ${getSpecialLabel('max_characters', replacers, customTexts)}`;
      err = (err + valErr).trim();
      validationOk = false;
    }
  }

  if (!requiredOk) {
    err = customTexts.required_field;
  }

  if (setCtrlValidationError) setCtrlValidationError(err ?? '');
  newControl._validationError = err ?? null;
  
  return {
    requiredOk,
    validationOk,
    newControl,
  };
};

export const validateInventoryControl = async (ctrl, formId, userCustomTexts) => {
  let _validationError = null;

  if (ctrl.props.required && !ctrl.value) _validationError = userCustomTexts.required_field;
  if (ctrl.value) {
    const validationError = await checkProductAvailability(ctrl.value.products, formId);
    if (Object.keys(validationError).length) _validationError = validationError;
  }

  return { _validationError };
};

export const validatePresentPage = async (validationObj, skipChecks = false, isPayOnSubmission) => {
  const { responseStore = 'formplus', presentPage, hiddenFields, userCustomTexts, phoneNumberFields, setErrorMessage, formId } = validationObj;
  let atLeastOneWrong = false;
  let inventoryCtrl = null;
  const ctrls = []; // collection of controls validated and updated with property '_validationError'

  if (!skipChecks) {
    for (let i = 0; i < presentPage.controls.length; i += 1) {
      const ctrl = presentPage.controls[i];
      if (hiddenFields[ctrl.internal_id] || ctrl.props.is_inventory_payment) continue;

      if (ctrl.type === 'inventory_field') inventoryCtrl = cloneDeep(ctrl);

      if (ctrl.type === 'table_field') {
        const { tableError, newCtrl } = validateTableField(ctrl, userCustomTexts);
        ctrls.push(newCtrl);
        if (tableError) {
          atLeastOneWrong = true;
          setErrorMessage(userCustomTexts.required_provided_error);
        }
      } else {
        const phoneNumberFieldData = () => {
          const phoneNumberField = phoneNumberFields[ctrl.internal_id];
          if (!phoneNumberField || !ctrl.type === 'phone' || isEmpty(phoneNumberFields)) {
            return {};
          }

          return phoneNumberField;
        };

        const { requiredOk, validationOk, newControl } = controlValidate({
          phoneNumberData: phoneNumberFieldData(),
          isPayOnSubmission,
          userCustomTexts,
          responseStore,
          ctrl
        });

        ctrls.push(newControl);

        if (!requiredOk && !validationOk) {
          atLeastOneWrong = true;
          setErrorMessage(userCustomTexts.required_provided_error);
        } else if (!requiredOk) {
          atLeastOneWrong = true;
          setErrorMessage(userCustomTexts.required_error);
        } else if (!validationOk) {
          atLeastOneWrong = true;
          setErrorMessage(userCustomTexts.provided_error);
        }
      }
    }
  }

  if (inventoryCtrl) {
    const { _validationError } = await validateInventoryControl(inventoryCtrl, formId, userCustomTexts);
    if (_validationError) {
      inventoryCtrl._validationError = _validationError;
      ctrls.push(inventoryCtrl);
      atLeastOneWrong = true;
    }
  }

  return {
    atLeastOneWrong,
    ctrls,
  };
};

// specially created for use with props/parameters present only in signature and file_upload.jsx
function upload(
  ctrl,
  file,
  setUploadState,
  isOffline,
  setCtrlValidationError,
  setProgress,
  saveUpdatedCtrl,
  beginUpload,
  stopUpload
) {
  if (isEqual(ctrl.value?.filename, file.name)) return; // restrict uploading file with same name as current ctrl value

  beginUpload();
  setUploadState((prev) => ({ ...prev, uploading: true }));

  function handleErr(msg) {
    setCtrlValidationError(msg);
    uploader.removeFile(file);
  }

  const mimetype = file.type || 'application/octet-stream';
  fplsFetch({
    url: `/api/v2/public/attachment-upload/${window.FORM_ID}?mimetype=${mimetype}`,
    removeToken: true,
  })
    .then((response) => {
      const { upload_url, blob_name } = response;
      uploader.startUpload({
        url: upload_url,
        headers: { 'Content-Type': mimetype },
        method: 'PUT',
        concurrency: 1,
        type: 'File',
        onProgress(subs) {
          setProgress((prev) => ({
            ...prev,
            loaded: subs.loaded,
            humanSize: subs.humanSize,
          }));
          setTimeout(() => {
            if (isOffline) {
              const msg = 'Upload Timed Out! Kindly check your computer’s internet connection and try again';
              handleErr(msg);
            }
          }, 500);
        },
        onCompleted() {
          const fileInfo = generateFileInfo(blob_name, file);
          fplsFetch({
            method: 'POST',
            url: `/api/v2/public/validate-attachment-upload/${ctrl.props.file_field_id}`,
            data: { file_info: fileInfo },
            removeToken: true,
          })
            .then((resp) => {
              const rec = resp;
              setUploadState((prev) => ({
                ...prev,
                uploading: false,
                uploaded: true,
                uploadedFileName: rec?.filename,
              }));
              saveUpdatedCtrl(rec?.error ? null : rec);
            })
            .catch((err) => {
              console.error(`Error while validating file: ${err?.data?.error}`);
              // FIXME: Invalid file is not the only error,
              // spurious cloudstorage error might also cause the validation to fail
              const msg =
                `Invalid File! ${err?.data?.error}. ` +
                `Your file couldn’t be uploaded because it’s a corrupt ` +
                `file or in an unaccepted format. ` +
                `Kindly check and upload your file in the requested format.`;
              handleErr(msg);
            });
          stopUpload();
        },
        onError(err) {
          console.error(`Error while uploading file: ${err}`);
          const xhrAbortErr = 'request_aborted'; // Must be same as in UiUploader class
          let msg = '';
          if (isOffline) {
            msg = 'Upload Timed Out! Kindly check your computer’s internet connection and try again';
          } else {
            msg =
              'Upload Failed! ' +
              'You’ve encountered an error while uploading this file. ' +
              'Kindly re-upload your file. ' +
              'If this issue persists, ask form owner to contact support@formpl.us';
          }
          if (err === xhrAbortErr) {
            setUploadState((prev) => ({
              ...prev,
              uploading: false,
              uploaded: false,
            }));
          } else {
            handleErr(msg);
          }
          stopUpload();
        },
      });
    })
    .catch((error) => {
      if (error.status === -1) {
        setUploadState((prev) => ({
          ...prev,
          uploading: false,
        }));
        console.error('AttachmentUrl.get error', error);
      }
      stopUpload();
    });
}

// specially created for use with props/parameters present only in file_upload.jsx
export const selectFile = (uploadProps) => {
  const {
    col,
    ctrl,
    inTable,
    selected,
    isOffline,
    stopUpload,
    beginUpload,
    setProgress,
    setUploadState,
    setCurrentFile,
    saveUpdatedCtrl,
    setCtrlValidationError,
  } = uploadProps;

  const getCtrlProps = () => (inTable ? ctrl.table_props[col - 1] : ctrl.props);
  const controlprops = getCtrlProps();

  const file = selected;
  uploader.removeAll();

  function validateFileExtensions() {
    const selectedFileExtension = file.name.split('.').pop();
    // Check if file extension is in allowed extensions list defined by form creator
    if (
      controlprops.file_extensions.join() === '' ||
      controlprops.file_extensions?.join().includes(selectedFileExtension)
    ) {
      return true;
    }

    return false;
  }

  function validateFileCategories() {
    const categoryMimeTypes = {
      IMAGES: ['image/gif', 'image/x-icon', 'image/jpeg', 'image/png', 'image/svg+xml', 'image/tiff', 'image/webp'],
      VIDEOS: [
        'video/x-msvideo',
        'video/mpeg',
        'video/ogg',
        'video/webm',
        'video/3gpp',
        'video/3gpp2',
        'video/mp4',
        'video/avi',
        'video/x-flv',
        'video/x-matroska',
        'video/quicktime',
      ],
      DOCUMENTS: [
        'application/x-abiword',
        'application/octet-stream',
        'text/csv',
        'application/vnd.oasis.opendocument.presentation',
        'application/vnd.oasis.opendocument.spreadsheet',
        'application/vnd.oasis.opendocument.text',
        'application/pdf',
        'application/x-shockwave-flash',
        'text/plain',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/vnd.visio',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ],
    };

    let validationCategories = [];
    // extract mimetypes based on each set file categories
    for (let i = 0; i < controlprops.file_categories.length; i += 1) {
      validationCategories = validationCategories.concat(categoryMimeTypes[controlprops.file_categories[i]]);
    }

    // check if selected file type is included in extracted mimetypes
    if (validationCategories.includes(file.type.toLowerCase())) {
      return true;
    }
    return false;
  }

  if (file) {
    // Validate 0 byte
    if (file.size <= 0) {
      setCtrlValidationError(
        'Invalid File! ' +
          'Your file couldn’t be uploaded because it’s a corrupt file or in an unaccepted format. ' +
          'Kindly check and upload your file in the requested format.'
      );
      return;
    }

    // Validate maximum size
    if (Number(controlprops.max_file_size) && file.size > controlprops.max_file_size) {
      const fileUnit = controlprops.max_file_size / 1000000 > 1000 ? 'GB' : 'MB';
      setCtrlValidationError(
        `Unable to upload file. Maximum file size is ${
          controlprops.max_file_size / 1000000
        }${fileUnit}. Please check and try and again.`
      );
      return;
    }

    // file acceptance error messages
    let fileExtensionMsg;
    let fileCategoryMsg;
    let fileSupplementaryMsg;

    try {
      const fileExtensions = controlprops.file_extensions ? controlprops.file_extensions.join(', ') : '';
      const fileCategory = controlprops.file_categories ? controlprops.file_categories.join(', ') : '';
      fileExtensionMsg = `You have selected an invalid file type. The allowed file extensions are : ${fileExtensions} . Please check and try again.`;
      fileCategoryMsg = `You have selected an invalid file type. The allowed file types are : ${fileCategory} . Please check and try again.`;
      fileSupplementaryMsg = `You have selected an invalid file type. The allowed file types and extensions are : ${fileCategory} - ${fileExtensions} . Please check and try again.`;
    } catch (err) {
      console.error('File error', err);
    }

    // Validate allowed file extensions only
    if (file && controlprops.file_extensions?.length > 0 && !controlprops.file_categories?.length > 0) {
      if (!validateFileExtensions()) {
        setCtrlValidationError(fileExtensionMsg);
        return;
      }
      setCtrlValidationError('');
    }

    // Validate file category only
    if (file && controlprops.file_categories?.length > 0 && !controlprops.file_extensions?.length > 0) {
      if (!validateFileCategories()) {
        setCtrlValidationError(fileCategoryMsg);
        return;
      }
      setCtrlValidationError('');
    }

    // If both file category and extensions are present, validate both supplementarily
    if (file && controlprops.file_extensions?.length > 0 && controlprops.file_categories?.length > 0) {
      if (!(validateFileCategories() || validateFileExtensions())) {
        setCtrlValidationError(fileSupplementaryMsg);
        return;
      }
      setCtrlValidationError('');
    }

    // If no extension and no categories, then zip unsupported files
    let zippedFiles = [];
    if (file && controlprops.file_extensions?.length === 0 && controlprops.file_categories?.length === 0) {
      if (isExtensionSupported(file.type)) {
        zippedFiles = zipFiles(file);
      }
    }

    if (zippedFiles.length > 0) {
      uploader.addFiles(zippedFiles);
    } else {
      uploader.addFiles([file]);
    }

    setCurrentFile(uploader.getFiles()[0]);

    // send file to service worker
    // const swData = {
    //   has_file: true,
    //   files: ctrl.files,
    //   field_id: ctrl.internal_id,
    // };
    // $scope.sendMessageToSw(swData);

    setTimeout(() => {
      upload(
        ctrl,
        file,
        setUploadState,
        isOffline,
        setCtrlValidationError,
        setProgress,
        saveUpdatedCtrl,
        beginUpload,
        stopUpload
      );
    });
  }
};

export const acceptSignature = ({
  ctrl,
  setUploadState,
  isOffline,
  setCtrlValidationError,
  dataURL,
  saveUpdatedCtrl,
  beginUpload,
  stopUpload
}) => {
  const file = dataURItoBlob(dataURL);
  file.name = 'Signature.png';
  uploader.addFiles([file]);
  upload(
    ctrl,
    file,
    setUploadState,
    isOffline,
    setCtrlValidationError,
    () => {},
    saveUpdatedCtrl,
    beginUpload,
    stopUpload
  );
};

export const publicBlobReaderApi = (blobPath, setSignature) => {
  fplsFetch({
    url: `/api/v2/public/file/${blobPath}`,
    removeToken: true,
  })
    .then((response) => {
      setSignature(response.serving_url);
    })
    .catch((error) => {
      console.error('Error reading blob', error);
    });
};

export const detectUniqueVisitor = (id) => {
  let unique_visit = true;
  if (
    document.cookie.split(';').filter((item) => {
      // check if current form id is present in cookies
      return item.indexOf(`${id}=`) >= 0;
    }).length
  ) {
    unique_visit = false;
  } else {
    // if form id not present, add it to cookies
    document.cookie = `${id}=1`;
  }
  return unique_visit;
};

export const detectDevice = () => {
  const windowWidth = window.screen.width;
  let device = '';
  if (windowWidth < 768) {
    device = 'mobile';
  }
  if (windowWidth >= 768) {
    device = 'desktop';
  }
  return device;
};

export const parseURL = (URL) => {
  const query = URL.substring(1);
  const parsedObject = {};
  const list = query.split('&');
  list.forEach(function (value) {
    const keyVal = value.split('=');
    // Fields that support multiple selection is serialized with a []
    // (which is encoded as %5B%5D) at the end of the key, hence this if block
    if (keyVal[0].match(/%5B%5D$/i)) {
      keyVal[0] = keyVal[0].substring(0, keyVal[0].length - 6);
    }
    if (keyVal[0] in parsedObject) {
      parsedObject[keyVal[0]] = `${parsedObject[keyVal[0]]},${keyVal[1]}`;
    } else {
      parsedObject[keyVal[0]] = keyVal[1];
    }
  });

  return parsedObject;
};

export const prepareFormPayload = (payload) => {
  const parsedURL = parseURL(window.location.search);
  const shufflableControls = [ControlType.IMAGE_CHOICE, ControlType.CHECKBOXES, ControlType.RADIO_BUTTONS];
  const newPayload = cloneDeep(payload);
  const { pages } = newPayload.definition;
  for (let p = 0; p < pages.length; p += 1) {
    const page = pages[p];

    // Shuffle/randomise page controls
    if (payload.form_options.randomise_question_order) {
      const fixableControls = [ControlType.MEDIA_HEADER, ControlType.DESCRIPTION];
      pages[p].controls = shuffleWithinGroups(page.controls, fixableControls);
    }

    pages[p].controls = page.controls.map((ctrl) => {
      const newCtrl = cloneDeep(ctrl);
      const {
        value,
        type,
        display_id,
        props: { options, rating_type, other, row_headers, column_headers },
      } = newCtrl;
      const isString = () => typeof value === 'string' || value instanceof String;

      // Reshuffle image_choices, checkboxes and radio if shuffle_options is enabled
      if (!value && newCtrl.props.shuffle_options && shufflableControls.includes(type)) {
        shuffleOptions(newCtrl);
      }

      // Initial matrix setup
      if (type === ControlType.MATRIX_RATING && (!value || value.length === 0)) {
        newCtrl.value = row_headers.map((header) => `${header}: undefined`);
      }

      // Initial datetime setup
      if (type === ControlType.DATETIME) {
        const {
          now,
          type: dateType,
          separator = '/',
          time_format = '24',
          disabled_dates = [],
          default: dateDefault,
          disable_year = false,
          disabled_date_range = '',
          date_format = 'YYYY/MM/DD',
        } = newCtrl.props;

        const disableYearEffect = dateType === DateTimeType.DATE && disable_year;

        if (!disableYearEffect) {
          // Refactor disabled_dates array
          if (disabled_dates?.length) {
            newCtrl.props.disabled_dates = disabled_dates.map((date) =>
              getFormattedDate(new Date(date), date_format, separator)
            );
          }

          // Refactor disabled_date_range
          if (disabled_date_range) {
            const rangeDates = disabled_date_range.split(' to ');
            newCtrl.props.disabled_date_range = rangeDates
              .map((date) => getFormattedDate(new Date(date), date_format, separator))
              .join(' to ');
          }
        }

        // Initialize value if not a saved/edited submission and default/now is set
        if (!value) {
          let dateTime = '';

          if (!disableYearEffect) {
            if (now) {
              const newDate = new Date();
              dateTime = getYearMonthDayTime(newDate, separator, dateType, time_format, date_format);
            } else if (dateDefault) {
              if (dateType === DateTimeType.DATE || dateType === DateTimeType.DATETIME) {
                const newDefault = dateType === DateTimeType.DATE ? dateDefault.split(' ')[0] : dateDefault;
                const newDate = new Date(newDefault);
                dateTime = getYearMonthDayTime(newDate, separator, dateType, time_format, date_format);
              } else if (dateType === DateTimeType.TIME) {
                dateTime = dateDefault;
              }
            }
          }

          newCtrl.value = dateTime;
        }
      }

      if (value && isString()) {
        // Parse values for file upload, signature, select multiple, address
        if (
          [
            ControlType.FILE_UPLOAD,
            ControlType.SIGNATURE,
            ControlType.SELECT_MULTIPLE,
            ControlType.ADDRESS,
            ControlType.INVENTORY,
          ].includes(type)
        ) {
          newCtrl.value = JSON.parse(value);
        }

        // Parse values for radio button fields if other is enabled
        if ((type === ControlType.RADIO_BUTTONS || type === ControlType.INLINE_RADIOS) && other?.enabled) {
          if (options.indexOf(value) < 0) {
            // If true, user_option has been found
            newCtrl[ControlType.OptOther.STRING] = true;
            newCtrl.value = value;
          }
        }

        // Parse and restructure values for image_choice fields
        if (type === ControlType.IMAGE_CHOICE && rating_type === 'checkbox') {
          const parsedValue = JSON.parse(value);
          const newValue = !Array.isArray(parsedValue) ? Object.keys(parsedValue) : parsedValue;
          newCtrl.value = newValue;
        }

        // Parse values for checkbox fields
        if (type === ControlType.CHECKBOXES || type === ControlType.INLINE_CHECKBOXES) {
          let parsedValue = JSON.parse(value);
          // Get all options in checkbox
          const newKeys = Object.keys(parsedValue ?? {});
          if (newKeys.length > 0) {
            const valueObj = {};
            for (let k = 0; k < newKeys.length; k += 1) {
              // Check if each option is present in ctrl.props.options
              if (options?.includes(newKeys[k])) {
                // Assign true to option
                valueObj[newKeys[k]] = true;
              } else {
                // Check if option is __fpls_opt_other__
                if (newKeys[k] === ControlType.OptOther.STRING) {
                  valueObj[ControlType.OptOther.STRING] = parsedValue[newKeys[k]];
                } else {
                  valueObj[ControlType.OptOther.STRING] = newKeys[k];
                }
                newCtrl[ControlType.OptOther.STRING] = true;
              }
            }
            parsedValue = { ...valueObj };
          }
          newCtrl.value = parsedValue;
        }

        // Parse values for matrix rating fields
        if (type === ControlType.MATRIX_RATING) {
          const parsedValue = JSON.parse(value);
          const pathName = window.location.pathname;

          // edited form
          if (pathName.includes('/edit/')) {
            const newValue = row_headers.map((rowHeader, index) => {
              const colHeader = column_headers[parsedValue[index] - 1];
              return `${rowHeader}: ${colHeader ? colHeader : 'undefined'}`;
            });
            newCtrl.value = newValue;
          }

          // saved form
          if (pathName.includes('/saved/')) {
            const questions = parsedValue.map((line) => line.split(': ')[0]);

            // Blank placeholder '---' is for ui purposes alone (on the show/print response screen),
            // some previously stored values already include it, here we replace '---' by 'undefined'
            const answers = parsedValue.map((line) => {
              const splitValue = line.split(': ')[1];
              return splitValue === '---' ? 'undefined' : splitValue;
            });

            const newValue = questions.map((question, index) => {
              return `${question}: ${answers[index]}`;
            });

            newCtrl.value = newValue;
          }
        }

        // Parse values for ranking fields
        if (type === ControlType.RANKING) {
          const newValue = JSON.parse(value);
          newCtrl.ranking_options = newValue;
          newCtrl.value = newValue;

          // Check if 'other.enabled' is true and add user_option if necessary
          if (newValue && newValue.length > 0 && other.enabled) {
            // Restore blank input other option field
            if (newValue.length === options.length) newValue.push('');

            // Find user_option and update ranking_options
            newValue.forEach((val, index) => {
              if (!options.includes(val)) {
                // If true, user_option has been found
                newCtrl.user_option = val;

                // Update ranking_options and props.options
                // Add ControlType.OptOther.STRING at the position of the value of user_option
                newCtrl.ranking_options[index] = ControlType.OptOther.STRING;
              }
            });
          }
        }

        // Parse values for lookup field with select multiple or checkbox field types
        if (
          type === ControlType.LOOKUP &&
          [ControlType.SELECT_MULTIPLE, ControlType.CHECKBOXES].includes(newCtrl.props.field_type)
        ) {
          try {
            newCtrl.value = JSON.parse(value.replaceAll("'", '"'));
            if (typeof newCtrl.value === 'number') {
              newCtrl.value = value.toString().split(/\r?\n/);
            }
          } catch {
            newCtrl.value = value.replaceAll("'", '"').split(/\r?\n/);
          }
        }

        // Parse value for table field
        if (type === ControlType.TABLE_FIELD && value && isString()) {
          const parsedTable = JSON.parse(value);
          const lastItems = parsedTable[parsedTable.length - 1];
          // Check if the table field has simple calculation(s)
          // Calculation terms (SUM or AVERAGE) will be present for saved/edited forms where any column has a calculation
          try {
            if (lastItems.find((item) => item?.includes('SUM - ') || item?.includes('AVERAGE - '))) {
              parsedTable.splice(parsedTable.length - 1, 1);
            }
            newCtrl.value = parsedTable;
          } catch {
            newCtrl.value = parsedTable;
          }
        }

        // Reshuffle parsed values for image_choices, checkboxes and radio if shuffle_options is enabled
        if (newCtrl.props.shuffle_options && shufflableControls.includes(type)) {
          shuffleOptions(newCtrl);
        }
      }

      // setting prefill value
      if (parsedURL && parsedURL[display_id]) {
        const decodedValue = decodeURIComponent(parsedURL[display_id]);

        if (
          type === ControlType.SELECT_BASIC ||
          type === ControlType.RADIO_BUTTONS ||
          type === ControlType.INLINE_RADIOS
        ) {
          newCtrl.value = options[decodedValue];
        } else if (
          type === ControlType.CHECKBOXES ||
          type === ControlType.INLINE_CHECKBOXES ||
          type === ControlType.SELECT_MULTIPLE
        ) {
          const optionsIDs = decodedValue.split(',');
          if (type === ControlType.SELECT_MULTIPLE) {
            newCtrl.value = optionsIDs.map((option) => options[option.trim()]);
          } else {
            newCtrl.value = {};
            optionsIDs.forEach((option) => {
              newCtrl.value[options[option.trim()]] = true;
            });
          }
        } else {
          newCtrl.value = decodedValue;
        }
      }

      return newCtrl;
    });
  }

  // Absorb current values (before any update) as initial response for the next submission
  // Initial response will be an object of nulls for a new form or of previous values for an edited form
  const initialResponse = {};
  newPayload.definition.pages.forEach((p) => {
    p.controls.forEach((c) => {
      initialResponse[c.internal_id] = c.value;
    });
  });

  return {
    newPayload,
    initialResponse,
  };
};

const getTableColResult = (columnType, columnCtrlValue, optionValues, weights = [], options = []) => {
  const {
    NUMBER,
    CHECKBOXES,
    STAR_RATING,
    SCALE_RATING,
    SELECT_BASIC,
    HEART_RATING,
    RADIO_BUTTONS,
    INLINE_RADIOS,
    INLINE_CHECKBOXES,
  } = ControlType;

  let result = 0;
  let weight = 1;

  if (!columnCtrlValue) return result;

  switch (columnType) {
    case NUMBER:
      result = columnCtrlValue;
      break;
    case HEART_RATING:
    case STAR_RATING:
    case SCALE_RATING:
      result = parseInt(columnCtrlValue.replace(/\D/g, ''), 10);
      weight = weights[result - 1] || weights[0] || 1;
      break;
    case SELECT_BASIC:
    case RADIO_BUTTONS:
    case INLINE_RADIOS:
      result = 1;
      weight = weights.find((w, i) => columnCtrlValue === options[i]) || 1;
      break;
    case CHECKBOXES:
    case INLINE_CHECKBOXES:
      result = weights.every((w) => w === 1) ? columnCtrlValue.length : 1;
      weight = columnCtrlValue.reduce((acc, val) => acc + weights[options.indexOf(val)], 0);
      break;
  }

  return optionValues ? result * weight : result;
};

export const getSumOrAverage = (ctrl, index, operation) => {
  const {
    table_props,
    value: tableValues,
    props: { column_types },
  } = ctrl;
  const { options, weights, option_values: optionValues } = table_props[index];
  const columnType = column_types[index];

  const arr = (tableValues || []).map((row) => {
    const columnCtrlValue = row[index];
    return Number(getTableColResult(columnType, columnCtrlValue, optionValues, weights, options))
  });

  const sum = arr.reduce((a, b) => a + b, 0);
  const avg = sum / arr.length;

  const roundedValue = (val) => (decimalCount(val) ? val.toFixed(2) : val);

  return operation === 'sum' ? roundedValue(sum) : roundedValue(avg || sum);
};

export const doSaveFormForLater = async ({
  form,
  email,
  onSavedFormPage,
  savedFormID,
  setSavedFormID,
  setLoading,
  setSaveFormError,
}) => {
  let savedFormSubmissionId;
  const { savedFormPaymentSubmissionId } = form;
  let payload;

  // If on a previously saved form page,
  // or if not on a saved form but form already has saved link
  // and changes have been made to the form results
  // after the triggering save and continue the first time,
  // update the form instead
  if (onSavedFormPage || savedFormID) {
    payload = {
      form_id: window.FORM_ID,
      saved_response_key: savedFormID || window.location.pathname.split('/').pop(),
      results: form.editableForm.definition.pages,
      form_modified: form.form.modified,
    };

    try {
      const response = await fplsFetch({
        method: 'PUT',
        url: `/api/v2/public/forms/${window.FORM_ID}/save_submission`,
        data: payload,
        removeToken: true,
      });

      if (response) {
        setLoading(false);
        savedFormSubmissionId = response.saved_submission_id;
        setSavedFormID(savedFormSubmissionId);
      }
    } catch (error) {
      setLoading(false);
      setSaveFormError(true);
      console.error('Save later error', error);
    }
  }
  // When form has no saved link and the save and
  // continue button is triggered the very first time
  else if (!onSavedFormPage) {
    payload = {
      form_id: window.FORM_ID,
      results: form.editableForm.definition.pages,
      email,
      // respondent_email: form.form.respondent_email,
      respondent_email: form.respondentEmail || '',
      form_modified: form.form.modified,
      saved_payment_submission_id: savedFormPaymentSubmissionId || '',
    };

    try {
      const response = await fplsFetch({
        method: 'POST',
        url: `/api/v2/public/forms/${window.FORM_ID}/save_submission`,
        data: payload,
        removeToken: true,
      });

      if (response) {
        setLoading(false);
        if (response.error) {
          setSaveFormError(true);
          console.error('Save later error', response.error);
        } else {
          savedFormSubmissionId = response.saved_submission_id;
          setSavedFormID(savedFormSubmissionId);
        }
      }
    } catch (error) {
      setLoading(false);
      setSaveFormError(true);
      console.error('Save later error', error);
    }
  }
};

export const registerSW = () => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw.js', { scope: '/form' });
  }
};

export class FormUtils {
  constructor() {
    this.sessionInterval = null;
    this.offlineMessage = false;
    this.editReviewParams = null;
    this.workflow = null;
    this.checkCaptcha = false;
    this.captchaResponse = '';
    this.invisibleCaptchaResp = '';
    this.currentInterval = 5000;
    this.stopPinging = false;
    this.timeElapsed = 0;
    this.coordinates = ['', ''];
    this.UNIQUE_SUBMISSIONS_IDS = [1645076009, 1616536001, 1643046007]; // test form
    this.formAnalytics = {
      form_id: window.FORM_ID,
      country: '',
      device: detectDevice(),
      unique_visit: detectUniqueVisitor(window.FORM_ID),
      time_spent: 0,
      submitted: false,
      session_id: 0,
      browser: '',
      current_user: window.OWNER,
    };
    this.startDate = new Date();
    this.inactiveStartTime = null;
  }

  getSessionInterval = () => {
    return this.sessionInterval;
  };

  getElapsedTime = () => {
    const endDate = new Date();
    const focusTime = endDate.getTime() - this.startDate.getTime();
    this.timeElapsed += focusTime;
  };

  sendActionToInvisibleRecaptcha = (type) => {
    const promise = new Promise((resolve, reject) => {
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(window.FORM_RECAPTCHA_SITE_KEY_V3, { action: type })
          .then((token) => {
            resolve(token);
          })
          .catch((err) => reject(err));
      });
    });
    return promise;
  };

  sendSubmitEvent = (form) => {
    const userGATrackingID = form.form.form_options.ga_tracking_id;
    if (!navigator.userAgent.includes('saturn-dnt') && userGATrackingID.length > 0) {
      window.ga('userTracker.send', 'event', 'submit', 'click', form.form.name);
    }
  };

  isOffline = () => {
    return navigator.onLine === false;
  };

  browserIsSafari = () => {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.indexOf('safari') !== -1) {
      return ua.indexOf('chrome') === -1;
    }
    return false;
  };

  storeInCookie = () => {
    document.cookie = `${window.FORM_ID}-submitted=1`;
  };

  isSupport = () => {
    // identify formplus support to exclude them from form analytics
    return navigator.userAgent.includes('saturn-dnt');
  };

  inIframe = () => {
    // to determine if the form is being loaded in an iframe or not.
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  };

  calculateIntervalBackoff = () => {
    // returns delay time between intervals of sending `total time spent` to server
    // timeElapsed represents the overall time spent on the form
    if (this.timeElapsed > 180000) {
      // after three minutes, poll once in a minute
      return 60000;
    }
    if (this.timeElapsed > 120000) {
      // after two minutes, poll once in 30 seconds
      return 30000;
    }
    if (this.timeElapsed > 60000) {
      // after one minute, poll once in 15 seconds
      return 15000;
    }
    return 5000; // poll once every 5 seconds
  };

  cancelInterval = () => {
    clearTimeout(this.sessionInterval);
  };

  sendSessionTime = () => {
    const payload = {
      form_id: window.FORM_ID,
      time_spent: Math.round(this.timeElapsed / 1000),
      session_id: this.formAnalytics.session_id,
      current_user: window.OWNER,
    };
    // send `total time spent on form` to server
    this.getElapsedTime();
    this.startDate = new Date();
    fplsFetch({
      method: 'PUT',
      url: `/api/v2/public/forms/${window.FORM_ID}/analytics`,
      data: payload,
      removeToken: true,
    })
      .then((response) => {
        if (response.status === 'rejected') {
          this.stopPinging = true;
          this.cancelInterval();
        } else {
          this.cancelInterval();
          this.currentInterval = this.calculateIntervalBackoff();
          // start polling server with new interval
          this.startNewInterval();
        }
      })
      .catch((error) => {
        console.error('session error', error);
        this.cancelInterval();
        this.currentInterval = this.calculateIntervalBackoff();
        this.startNewInterval();
      });
  };

  startNewInterval = () => {
    if (
      this.isSupport() ||
      !this.formAnalytics.session_id ||
      (this.inIframe() &&
        (document.referrer.indexOf('formpl.us/control') > -1 || document.referrer.indexOf('formpl.us/r') > -1))
    ) {
      return;
    }
    this.sessionInterval = setTimeout(this.sendSessionTime, this.currentInterval);
  };

  sendAnalytics = () => {
    // send analytics data to the server
    if (
      this.isSupport() ||
      (this.inIframe() &&
        (document.referrer.indexOf('formpl.us/control') > -1 || document.referrer.indexOf('formpl.us/r') > -1))
    ) {
      return;
    }
    fplsFetch({
      method: 'POST',
      url: `/api/v2/public/forms/${window.FORM_ID}/analytics`,
      data: this.formAnalytics,
      removeToken: true,
    })
      .then((response) => {
        if (response.status === 'OK') {
          this.formAnalytics.session_id = response.session_id;
          if (!this.sessionInterval) {
            // start polling server with `total timespent on form`
            this.startNewInterval();
          }
        }
      })
      .catch((error) => {
        console.error('Send Analytics Error', error);
      });
  };

  onFocusForm = () => {
    const focusCondition = {
      stopPinging: this.stopPinging,
      isSupport: this.isSupport(),
      inIframe: this.inIframe(),
      submittedForm: this.formAnalytics.submitted,
      sessionID: !this.formAnalytics.session_id,
      formAnalytics: !this.formAnalytics,
    };

    // resume monitoring time and polling server
    const totalInactiveTime = new Date().getTime() - this.inactiveStartTime;
    if (totalInactiveTime >= 1800000) {
      // reset total elapsed time for new session after 30 minutes of inactivity
      this.timeElapsed = 0;
    }
    this.startDate = new Date();

    // also prevent pinging in the cases when session_id is not available before focus
    if (Object.values(focusCondition).some((v) => v === true)) {
      return;
    }
    this.startNewInterval();
  };

  onBlurForm = () => {
    // stop monitoring time and polling server
    this.inactiveStartTime = new Date().getTime();
    this.cancelInterval();
    this.getElapsedTime();
  };

  hasFileUploadField = ({ pages }) => {
    let result = false;
    for (const page of pages) {
      for (const control of page.controls) {
        result = result || control.type === 'file_upload';
        if (result) {
          return result;
        }
      }
    }

    return result;
  };

  performSubmission = async ({
    id,
    form,
    payload,
    startTime,
    setLoading,
    setEditPath,
    formContext,
    finalMessage,
    formResponses,
    setSubmitError,
    isWorkflowForm,
    editReviewForm,
    userCustomTexts,
    hasFinalMessage,
    setFormSubmitted,
    setCaptchaUpdate,
    setFormResponses,
    setCanSeeFormBack,
    workflowProcessForm,
    setSubmitInProgress,
    setFinalRenderedMessage,
  }) => {
    const formAllowsOffline = form.formAllowsOffline || form.form.form_options.offline_enabled;
    let hasSubmitted = false;

    const updateFinalMessage = () => {
      if (hasFinalMessage) {
        try {
          const newMessage = Mustache.render(finalMessage, formContext);
          if (newMessage) setFinalRenderedMessage(newMessage);
        } catch (error) {
          console.error('Error rendering Mustache template:', error);
        }
      }
    };

    try {
      const response = await fplsFetch({
        method: 'POST',
        url: `/api/v2/public/forms/${id}/submissions`,
        data: payload,
        removeToken: true,
      });

      if (response.valid) {
        const resultID = response.result_row_id;
        const fullEditPath = `${window.location.origin}/form/${id}/edit/${resultID}`;
        formContext.submission_id = resultID;
        formContext.edit_response_link = fullEditPath;

        hasSubmitted = true;
        updateFinalMessage();
        setFormSubmitted(true);
        setEditPath(fullEditPath);
        setFormResponses(formResponses);

        this.getElapsedTime();
        this.formAnalytics.time_spent = Math.round(this.timeElapsed / 1000);
        this.formAnalytics.submitted = true;
        this.sendAnalytics();
        this.sendSubmitEvent(form);
        if (this.UNIQUE_SUBMISSIONS_IDS.indexOf(id) >= 0) {
          this.storeInCookie();
        }
        this.cancelInterval(); // cancel polling to server since session has ended
        if (process.env.NODE_ENV !== 'production') {
          const endTime = new Date().getTime();
          console.log(`Execution times: ${(endTime - startTime) / 1000}, tracking time: ${response.tracking_time}`);
        }
        if (isWorkflowForm || editReviewForm || workflowProcessForm) {
          // reload review page to show latest form review
          window.top.location.reload();
        }
      } else if (response.code >= 1 && response.code <= 3) {
        setSubmitError(response.message);
      } else if (response.code === 10) {
        // This means invisible reCaptcha response has failed to satisfy the threshold of 0.7.
        // Update form reCaptcha state to display visible reCaptcha challenge as a fallback.
        setCaptchaUpdate({ captchaResponse: '', checkCaptcha: true });
      } else {
        setSubmitError(`${userCustomTexts.submit_error}. ${response.message}`);
        setCanSeeFormBack(response.code !== 4);
      }
      return { submission: 'online' };
    } catch (error) {
      console.error('Error submitting form:', error);
      const online = await isOnline();
      if (online) {
        setSubmitError(userCustomTexts.submit_error + (error?.data?.message || ''));
        if (!hasSubmitted) setCanSeeFormBack(true);
      }
      if (!online && !this.browserIsSafari() && navigator.userAgent.indexOf('Edge') < 0 && formAllowsOffline) {
        setFormSubmitted(true);
        updateFinalMessage();
      }
    } finally {
      setLoading(false);
      setSubmitInProgress(false);
    }
  };

  resolveAddressValue = (addressValue) => {
    const addressLines = [];

    for (const [_, fieldData] of Object.entries(addressValue)) {
      const line = `${fieldData.label}: ${fieldData.value || '---'}`;
      addressLines.push(line);
    }

    return addressLines.join('<br>');
  };

  resolveTableValue = (tableValue, ctrl) => {
    const { row_headers, column_headers, show_row_headers, row_headers_column_header } = ctrl.props;
    const headerRow = show_row_headers ? [row_headers_column_header, ...column_headers] : column_headers;
    const value = cloneDeep(tableValue);
    const dataRows = [];
    const hasSimpleCalcResult = value.length > ctrl.value.length;

    const cleanupValues = (values) => {
      return values.map((row) => row.map((cell) => (cell?.hasOwnProperty('filename') ? cell.filename : cell)));
    };

    const cleanedValues = cleanupValues(value);

    // Prepare data rows
    cleanedValues.forEach((row, index) => {
      const dataRow = show_row_headers ? [row_headers[index] || '', ...row] : row;
      dataRows.push(dataRow);
    });

    // Adjust the last row if it's a calculation result
    if (hasSimpleCalcResult) {
      const calcResultRow = dataRows[dataRows.length - 1].map((res) => (res === 0 ? '-' : res));
      dataRows[dataRows.length - 1] = calcResultRow;
    }

    let tableHTML = `
      <style>
        table {
          width: 100%;
          border-collapse: collapse;
        }
        th {
          background-color: whitesmoke;
          text-align: left;
        }
        th, td {
          padding: 5px;
          min-width: 200px;
          border: 1px solid black;
        }
      </style>
    `;

    // Add header row
    tableHTML += '<table><thead><tr>';
    headerRow.forEach((header) => {
      tableHTML += `<th>${header}</th>`;
    });

    tableHTML += '</tr></thead><tbody>';

    // Add data rows
    dataRows.forEach((row) => {
      tableHTML += '<tr>';
      row.forEach((cell, index) => {
        tableHTML += `<td data-label="${headerRow[index]}">${cell}</td>`;
      });
      tableHTML += '</tr>';
    });
    tableHTML += '</tbody></table>';

    return tableHTML;
  };

  resolveMatrixValue = (matrixValue) => {
    const valueLines = matrixValue.split('<br>');
    const questions = valueLines.map((line) => line.split(': ')[0]);
    const answers = valueLines.map((line) => {
      const splitValue = line.split(': ')[1];
      return splitValue === 'undefined' ? '---' : splitValue;
    });

    const newValue = questions.map((question, index) => {
      return `${question}: ${answers[index]}`;
    });

    return newValue.join('<br>');
  };

  getPrintableResponse = (formPages, formResponses, formResults) => {
    for (let i = 0; i < formPages.length; i += 1) {
      const page = formPages[i];
      for (let j = 0; j < page.controls.length; j += 1) {
        const ctrl = formPages[i].controls[j];
        const ctrlCanHaveOwnValue = 'value' in ctrl && ctrl.props.label !== undefined && ctrl.type !== ControlType.ROW;
        if (ctrlCanHaveOwnValue) {
          let value = formResults[i][j];
          if (!value || isEmpty(value)) {
            value = '---';
          }
          // Value is not a string
          else if (typeof value !== 'string') {
            // Value is not an array, must be an object
            if (!value.shift) {
              if (ctrl.type === ControlType.ADDRESS) {
                value = this.resolveAddressValue(value);
              } else {
                // For file upload and signature
                value = value.filename;
              }
            }
            // For table field: value is an array
            else if (value.shift && ctrl.type === ControlType.TABLE_FIELD) {
              value = this.resolveTableValue(value, ctrl);
            }

            // Other array type values
            else {
              value = value.join('<br>');
            }
          }

          if (ctrl.type === ControlType.MATRIX_RATING) {
            value = this.resolveMatrixValue(value);
          }

          formResponses.push({
            label: ctrl.props.label || ctrlDefaults()[ctrl.type],
            value,
          });
        }
      }
    }
  };

  clearControlValue = (ctrl) => {
    if (ctrl.type === 'table_field') {
      ctrl.value = setTableCtrlValueToNull(ctrl);
    } else {
      ctrl.value = null;
    }
    return ctrl;
  };

  submitForm = ({
    form,
    setLoading,
    setEditPath,
    enableTimer,
    timerPurpose,
    finalMessage,
    setSubmitError,
    isEditedByUser,
    hasFinalMessage,
    setErrorMessage,
    setCaptchaUpdate,
    setFormResponses,
    setFormSubmitted,
    showPrintResponse,
    setFoundEmptyForm,
    setCanSeeFormBack,
    setSubmitInProgress,
    isTimerEndForceSubmit,
    respondentQuizTimerInfo,
    setFinalRenderedMessage,
  }) => {
    const isFormplusTemplate = form.form.owner_user_id === 'aglzfmZvcm1wb2NyEQsSBFVzZXIYgICQgLbDvAsM';
    const {
      hiddenFields,
      userCustomTexts,
      initialResponse,
      formGeolocation,
      editableForm: {
        name,
        definition: { pages },
      },
    } = form;

    const { lat, lng } = formGeolocation;
    if (lat && lng) this.coordinates = [lat.toString(), lng.toString()];

    const doSubmit = () => {
      const startTime = new Date().getTime();
      const emptyArray = [];
      const formResults = [];
      const formContext = {};
      formContext.form_name = name;
      const formResponses = [];
      const formID = window.FORM_ID;

      const formPages = cloneDeep(pages).map((page) => {
        // clean up values for fields on hidden pages before submisssion
        if (hiddenFields[page.internal_id]) {
          page.controls.map((ctrl) => {
            this.clearControlValue(ctrl);
          });
        } else {
          // Since the default text prop (if available)
          // is used as the ctrl.value fallback in logic
          // clean up values for hidden fields before submisssion
          page.controls.map((ctrl) => {
            if (hiddenFields[ctrl.internal_id]) {
              this.clearControlValue(ctrl);
            }
          });
        }
        return page;
      });

      for (let i = 0; i < formPages.length; i += 1) {
        const page = formPages[i];
        const indexedFormResults = [];
        for (let j = 0; j < page.controls.length; j += 1) {
          const ctrl = formPages[i].controls[j];
          const {
            table_props,
            type: fieldType,
            value: fieldValue,
            props: { column_headers },
          } = ctrl;

          if (fieldType === ControlType.TABLE_FIELD && fieldValue) {
            emptyArray.push(...fieldValue.flat()); // Add each array value for rows in table to empty field tracker

            const sumOrAverageResults = [];
            for (let v = 0; v < column_headers.length; v += 1) {
              const { column_calculation } = table_props[v];
              let result;

              if (column_calculation === 1) {
                result = getSumOrAverage(ctrl, v, 'sum');
                sumOrAverageResults.push(`SUM - ${result}`);
              } else if (column_calculation === 2) {
                result = getSumOrAverage(ctrl, v, 'average');
                sumOrAverageResults.push(`AVERAGE - ${result}`);
              } else {
                sumOrAverageResults.push(0);
              }
            }

            if (sumOrAverageResults.some((element) => element !== 0)) {
              // If table field has simple calculation results
              indexedFormResults.push([...fieldValue, sumOrAverageResults]);
            } else {
              indexedFormResults.push(fieldValue);
            }
          } else {
            emptyArray.push(fieldValue);

            // Get new checkbox field value - convert object to array
            if (fieldType === ControlType.CHECKBOXES || fieldType === ControlType.INLINE_CHECKBOXES) {
              const checkboxValue = fieldValue;
              const checkboxArray = Object.keys(checkboxValue ?? {});
              if (checkboxArray?.includes(ControlType.OptOther.STRING)) {
                const ind = checkboxArray.findIndex((option) => option === ControlType.OptOther.STRING);
                checkboxArray[ind] = checkboxValue.__fpls_opt_other__;
              }
              indexedFormResults.push(checkboxArray);
            } else {
              indexedFormResults.push(fieldValue);
            }
          }
        }
        // Get results for all fields in the form
        formResults.push(indexedFormResults);
      }

      // Check if all field values are not empty
      const invalidValues = [[], '', 0, null];
      const decimalRegex = /^0(\.0+)?$/;

      const isFormEmpty = emptyArray.every((value) => {
        // Resolve quiz forms first
        if (isTimerEndForceSubmit && timerPurpose === 'quiz') return false;

        // Then
        if (invalidValues.includes(value)) return true;
        if (Array.isArray(value) && value.length === 0) return true;
        if (typeof value === 'string' && decimalRegex.test(value)) return true;
        return false;
      });

      const shouldAbortSubmission = isTimerEndForceSubmit && timerPurpose === 'event' && isFormEmpty;

      if (shouldAbortSubmission) {
        setFoundEmptyForm(true);
        return; // Abort submission
      }

      // Get form context
      formPages.forEach((page, pageIdx) => {
        page.controls.forEach((ctrl, ctrlIdx) => {
          formContext[`F${ctrl.internal_id}`] = formResults[pageIdx][ctrlIdx];
        });
      });

      // get printable responses
      if (showPrintResponse) this.getPrintableResponse(formPages, formResponses, formResults);

      if (isFormEmpty) {
        // If all field values are empty, display error prompt for submitting empty form
        setErrorMessage(userCustomTexts.empty_form);
        setSubmitInProgress(false);
      } else {
        // If all field values are not empty - at least one field value is present, submit form
        setLoading(true);
        const isJsonString = () => {
          const windowLocation = window.location.href.split('?')[1].split('=')[1];
          try {
            return JSON.parse(decodeURIComponent(windowLocation));
          } catch {
            return decodeURIComponent(windowLocation);
          }
        };

        const workflowProcessForm = window.location.href.indexOf('?processId=') > -1;

        // means the form is currently loaded from the workflow page, if true
        const isWorkflowForm =
          window.location !== window.parent.location && window.location.hash.indexOf('workflow') > -1;
        if (isWorkflowForm) {
          this.workflow = isJsonString();
          this.workflow.approved = '';
        } else {
          this.workflow = {};
        }

        const editReviewForm = /\/form\/\d{1,}\/edit\/\d{1,}\?params=/.test(window.location.href);
        if (editReviewForm) this.editReviewParams = isJsonString();

        const submissionUtils = new SubmissionUtils();
        const processId = window.location.search.split('processId=')[1];
        const savedRespKey =
          window.location.pathname.indexOf('saved') > -1 ? window.location.pathname.split('/').pop() : '';
        const editRespKey =
          window.location.pathname.indexOf('edit') > -1 ? window.location.pathname.split('/').pop() : '';

        let submissionPayload = {
          form_id: formID,
          process_id: processId || null,
          coordinates: this.coordinates,
          edit_response_key: editRespKey,
          saved_response_key: savedRespKey,
          initial_response: initialResponse,
          form_modified: form.form.modified,
          version_major: form.form.version_major,
          recaptcha_response: this.captchaResponse,
          sheet_schema: form.form.sheet_schema || '',
          respondent_email: form.respondentEmail || '',
          target_sheet_id: form.form.target_sheet_id || 0,
          is_forced_submission: isTimerEndForceSubmit || false,
          current_user: window.USER ? window.USER.user_id : null,
          invisible_recaptcha_response: this.invisibleCaptchaResp,
          results: submissionUtils.transformV1toV2(formResults, formPages),
          saved_payment_submission_id: form.savedFormPaymentSubmissionId || '',
          ...(!isEditedByUser &&
            enableTimer &&
            timerPurpose === 'quiz' && {
              respondent_unique_id: respondentQuizTimerInfo.quizId,
              time_elapsed: respondentQuizTimerInfo.timeElapsed,
            }),
        };

        if (editReviewForm) {
          submissionPayload = {
            ...submissionPayload,
            approved: this.workflow.approved,
            index: this.editReviewParams.index,
            step_id: this.editReviewParams.step,
            comment_id: this.editReviewParams.id,
            process_id: this.editReviewParams.process,
            is_edit_review: !this.editReviewParams.embedded,
          };
        }

        this.performSubmission({
          form,
          startTime,
          id: formID,
          setLoading,
          formContext,
          setEditPath,
          finalMessage,
          formResponses,
          isWorkflowForm,
          editReviewForm,
          setSubmitError,
          hasFinalMessage,
          userCustomTexts,
          setFormSubmitted,
          setFormResponses,
          setCaptchaUpdate,
          setCanSeeFormBack,
          workflowProcessForm,
          setSubmitInProgress,
          setFinalRenderedMessage,
          payload: submissionPayload,
        });
      }
    };

    const startSubmit = async () => {
      setSubmitError('');

      const { captchaResponse, checkCaptcha } = form.visibleCaptcha;
      this.invisibleCaptchaResp = '';
      this.checkCaptcha = checkCaptcha;
      this.captchaResponse = captchaResponse;

      const isProduction = process.env.NODE_ENV === 'production';

      try {
        if (checkCaptcha) {
          this.captchaResponse = this.isOffline() ? '[[offline-form]]' : captchaResponse;
          this.captchaResponse.length > 0 ? doSubmit() : setCaptchaUpdate({ captchaResponse });
        } else {
          this.invisibleCaptchaResp = isProduction
            ? this.isOffline()
              ? '[[offline-form]]'
              : await this.sendActionToInvisibleRecaptcha('submission')
            : '[[offline-form]]';
          doSubmit();
        }
      } catch (error) {
        setSubmitError('Please resolve Captcha.');
        console.error('Failed token get', error);
      }
    };

    startSubmit();
  };
}

export default {
  zipFiles,
  FormUtils,
  selectFile,
  getStateValue,
  controlValidate,
  acceptSignature,
  validateTableField,
  prepareFormPayload,
  doSaveFormForLater,
  publicBlobReaderApi,
  getYearMonthDayTime,
  isExtensionSupported,
};
